import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Database, Download, RotateCcw, Plus, Shield } from 'lucide-react';

const BYTES_PER_KB = 1024;
const BYTES_PER_MB = 1024 * 1024;

function formatBytes(bytes) {
  if (bytes < BYTES_PER_KB) return `${bytes} B`;
  if (bytes < BYTES_PER_MB) return `${(bytes / BYTES_PER_KB).toFixed(1)} KB`;
  return `${(bytes / BYTES_PER_MB).toFixed(1)} MB`;
}

function BackupCard({ backup, onDownload, onRestore }) {
  const [restoreOpen, setRestoreOpen] = useState(false);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`backup-${backup.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px] capitalize">{backup.type}</Badge>
            <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{backup.status}</Badge>
          </div>
          <p className="text-sm font-medium text-slate-800 mt-2">{new Date(backup.created_at).toLocaleString()}</p>
          <p className="text-xs text-slate-500 mt-1">
            {backup.total_records} records | {formatBytes(backup.size)} | by {backup.created_by_name}
          </p>
        </div>
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => onDownload(backup.id)} data-testid={`download-backup-${backup.id}`}>
            <Download className="h-3 w-3 mr-1" />Download
          </Button>
          <Dialog open={restoreOpen} onOpenChange={setRestoreOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="h-8 text-xs text-amber-600 border-amber-200 hover:bg-amber-50" data-testid={`restore-trigger-${backup.id}`}>
                <RotateCcw className="h-3 w-3 mr-1" />Restore
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="restore-confirm-dialog">
              <DialogHeader><DialogTitle>Restore Backup</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-slate-600">This will replace ALL current data with this backup. This action cannot be undone.</p>
                <p className="text-xs text-slate-500">Backup: {new Date(backup.created_at).toLocaleString()} ({backup.total_records} records)</p>
                <div className="flex gap-2">
                  <Button className="bg-amber-600 hover:bg-amber-700 flex-1" onClick={() => { onRestore(backup.id); setRestoreOpen(false); }} data-testid="confirm-restore-button">
                    <RotateCcw className="h-4 w-4 mr-2" />Confirm Restore
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={() => setRestoreOpen(false)}>Cancel</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}

export default function BackupPage() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const fetchBackups = useCallback(async () => {
    try {
      const resp = await api.get('/admin/backups');
      setBackups(resp.data);
    } catch {
      toast.error('Failed to load backups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBackups(); }, [fetchBackups]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await api.post('/admin/backups');
      toast.success('Backup created');
      fetchBackups();
    } catch {
      toast.error('Backup failed');
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = async (id) => {
    try {
      const resp = await api.get(`/admin/backups/${id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a'); a.href = url; a.download = `backup_${id.slice(0, 8)}.json`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const handleRestore = async (id) => {
    try {
      await api.post(`/admin/backups/${id}/restore`);
      toast.success('Backup restored successfully');
    } catch {
      toast.error('Restore failed');
    }
  };

  const lastBackup = backups.length > 0 ? backups[0] : null;

  return (
    <Layout>
      <div className="space-y-6" data-testid="backup-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              <Shield className="inline h-6 w-6 mr-2 text-blue-600" />Backup & Recovery
            </h1>
            <p className="text-sm text-slate-500 mt-1">Manage project data backups</p>
          </div>
          <Button onClick={handleCreate} disabled={creating} className="bg-blue-600 hover:bg-blue-700" data-testid="create-backup-button">
            <Plus className="h-4 w-4 mr-1" />{creating ? 'Creating...' : 'Create Backup'}
          </Button>
        </div>

        {/* Status card */}
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <Database className="h-5 w-5 text-blue-600" />
            <h2 className="font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>Backup Status</h2>
          </div>
          {lastBackup ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">Last Backup</p>
                <p className="text-sm font-medium text-slate-800">{new Date(lastBackup.created_at).toLocaleDateString()}</p>
                <p className="text-xs text-slate-500">{new Date(lastBackup.created_at).toLocaleTimeString()}</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">Type</p>
                <p className="text-sm font-medium text-slate-800 capitalize">{lastBackup.type}</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">Size</p>
                <p className="text-sm font-medium text-slate-800">{formatBytes(lastBackup.size)}</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">Records</p>
                <p className="text-sm font-medium text-slate-800">{lastBackup.total_records}</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-400">No backups yet. Create your first backup above.</p>
          )}
        </div>

        {/* Backup list */}
        <div>
          <h2 className="font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit' }}>Backup History ({backups.length})</h2>
          {(() => {
            if (loading) return <div className="text-center py-8 text-slate-400">Loading...</div>;
            if (backups.length === 0) return <div className="text-center py-8 text-slate-400">No backups created yet</div>;
            return (
              <div className="space-y-3">
                {backups.map(b => <BackupCard key={b.id} backup={b} onDownload={handleDownload} onRestore={handleRestore} />)}
              </div>
            );
          })()}
        </div>
      </div>
    </Layout>
  );
}
